import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {catchError, map, of, switchMap, withLatestFrom} from 'rxjs';
import {KillSwitchActions} from './kill-switch.actions';
import {selectIsEmergencyStopActive} from './kill-switch.selectors';
import {
  KILL_SWITCH_STATE_SERVICE_INJECTION_TOKEN,
  KillSwitchStateService
} from '../../../core/services/api/kill-switch-state-service.interface';

@Injectable()
export class KillSwitchEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private killSwitchStateService = inject<KillSwitchStateService>(KILL_SWITCH_STATE_SERVICE_INJECTION_TOKEN);


  public loadKillSwitch$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KillSwitchActions.public.loadKillSwitch),
      switchMap(() => this.killSwitchStateService.getKillSwitch().pipe(
        map((responseDto) => KillSwitchActions.private.loadKillSwitchSuccess({responseDto: responseDto})),
        catchError((error) => of(KillSwitchActions.private.loadKillSwitchFailure({error}))),
      )),
    );
  });

  public triggerKillSwitch$ = createEffect(() => this.actions$.pipe(
    ofType(KillSwitchActions.public.triggerKillSwitch),
    withLatestFrom(this.store.select(selectIsEmergencyStopActive)),
    switchMap(([props, isEmergencyStopActive]) => {
      if (!props.value && isEmergencyStopActive) {
        return of(KillSwitchActions.private.triggerKillSwitchFailure({
          attemptedValue: props.value,
          error: { message: 'Kill switch cannot be deactivated while emergency stop is active' },
        }));
      }

      return this.killSwitchStateService.triggerKillSwitch({
        value: props.value,
      }).pipe(
        map((responseDto) => KillSwitchActions.private.triggerKillSwitchSuccess({
          responseDto: responseDto,
        })),
        catchError((error) => of(KillSwitchActions.private.triggerKillSwitchFailure({
          attemptedValue: props.value,
          error: error,
        }))),
      );
    }),
  ));

  public refreshKillSwitchDataFromWebSocket$ = createEffect(() => this.actions$.pipe(
    ofType(KillSwitchActions.public.refreshKillSwitchDataFromWebSocket),
    map((_) => KillSwitchActions.public.loadKillSwitch())
  ));

  public loadEmergencyStop$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(KillSwitchActions.public.loadEmergencyStop),
      switchMap(() => this.killSwitchStateService.getEmergencyStop().pipe(
        map((responseDto) => KillSwitchActions.private.loadEmergencyStopSuccess({responseDto: responseDto})),
        catchError((error) => of(KillSwitchActions.private.loadEmergencyStopFailure({error}))),
      )),
    );
  });

  public triggerEmergencyStop$ = createEffect(() => this.actions$.pipe(
    ofType(KillSwitchActions.public.triggerEmergencyStop),
    switchMap(() => this.killSwitchStateService.triggerEmergencyStop().pipe(
      map((responseDto) => KillSwitchActions.private.triggerEmergencyStopSuccess({
        responseDto: responseDto,
      })),
      catchError((error) => of(KillSwitchActions.private.triggerEmergencyStopFailure({
        error: error,
      }))),
    )),
  ));
}
